import { GridWithGaps } from '@dreamstack/feature-components/src/layout/MainGrid'
import {
  Css_Text_16_24_tw,
  Css_Text_20_30_tw,
  Text_16_24,
} from '@dreamstack/feature-components/src/layout/Texts'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { SimpleTextLink } from '@dreamstack/simple-components/src/lib/SimpleTextLinks'
import { clsxMerge } from '@dreamstack/util'
import Image from 'next/image'
import type { FunctionComponent } from 'react'

const TwoColumnContainer = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      'flex flex-col w-full md:flex-row md:space-x-px-48 md:mt-px-32',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const Column = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      'flex-1 flex flex-col max-w-full justify-center text-left mt-px-48 md:mt-0',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const ImageWrapper = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      'h-[208px] md:h-[245px] ml:h-[335px] relative col-start-1 col-span-7',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const ContentWrapper = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      'col-span-6 z-10 bg-accentroGray-full text-accentroWhite-full',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const FloorWrapper = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge('flex flex-col ml-px-64 md:m-0', className)}
    {...rest}
  >
    {children}
  </div>
)

const HeadingWrapper = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge('flex flex-col md:flex-row my-px-24', className)}
    {...rest}
  >
    {children}
  </div>
)

const PropertyPrice = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      'text-accentroWhite-full text-px-28 leading-px-42 font-semibold',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const PropertyPreviewDiv = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      'flex flex-col w-full pt-0 px-px-24 pb-px-24 md:px-px-32 md:pb-px-40',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const PropertyHeading = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      Css_Text_20_30_tw,
      'flex-1 overflow-hidden overflow-ellipsis break-words text-accentroWhite-full font-semibold',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const PropertyBody = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsxMerge(
      Css_Text_16_24_tw,
      'flex-1 overflow-hidden overflow-ellipsis text-accentroWhite-full',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

type BadgeColors = 'default' | 'green'

const StyledBadge = ({
  smalltext,
  color,
  className,
  children,
  ...rest
}: React.PropsWithChildren<{
  smalltext: boolean
  color: BadgeColors
  className?: string
}>) => (
  <div
    className={clsxMerge(
      smalltext ? 'text-base' : 'text-2xl',
      color === 'default'
        ? 'bg-accentroAquaLight-full text-accentroGray-full'
        : '',
      color === 'green'
        ? 'bg-accentroLightGreen-full text-accentroWhite-full'
        : '',
      'rounded-full -rotate-12 absolute font-semibold w-[115px] h-[115px] flex flex-col justify-center items-center -bottom-px-16 -right-px-12 text-center z-50 md:right-px-24',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const properties = [
  {
    imgSrc: '/static/images/SideBySide1.jpeg',
    badgeContent: 'Vacant & Renovated',
    badgeColor: 'default',
    smalltext: true,
    heading: 'Torstraße 225e, Berlin-Mitte',
    sqrMeters: '64.08',
    floor: '3',
    price: '569,900.00',
    pricePerSqm: '8,893.57',
  },
  {
    imgSrc: '/static/images/SideBySide2.jpeg',
    badgeContent: 'Rented',
    badgeColor: 'green',
    smalltext: false,
    heading: 'Torstraße 225e, Berlin-Mitte',
    sqrMeters: '63.04',
    floor: '2',
    price: '264,410.00',
    pricePerSqm: '4,194.32',
    greenRectangle: '52% cheaper!',
  },
]

export const SideBySidePropertyComparison: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  return (
    <GridWithGaps>
      <div className="col-span-full">
        <TwoColumnContainer>
          {properties.map((property, index) => (
            <Column key={index}>
              <ImageWrapper>
                <Image
                  src={property.imgSrc}
                  alt="Property"
                  fill
                  style={{ objectFit: 'cover' }}
                />
                <StyledBadge
                  smalltext={property.smalltext}
                  color={property.badgeColor as BadgeColors}
                >
                  {property.badgeContent}
                </StyledBadge>
              </ImageWrapper>
              <ContentWrapper>
                <PropertyPreviewDiv>
                  <HeadingWrapper>
                    <PropertyHeading>
                      <div className="w-2/3 md:w-1/2 ml:w-full">
                        {property.heading}
                      </div>
                    </PropertyHeading>
                  </HeadingWrapper>
                  <div className="flex flex-row">
                    <div className="flex flex-col">
                      <PropertyHeading className="md:mr-px-80">
                        {property.sqrMeters} sqm
                      </PropertyHeading>
                      <PropertyBody>Living space</PropertyBody>
                    </div>
                    <FloorWrapper>
                      <PropertyHeading>{property.floor}</PropertyHeading>
                      <PropertyBody>Floor</PropertyBody>
                    </FloorWrapper>
                  </div>
                  {property.greenRectangle ? (
                    <div
                      className={clsxMerge(
                        Css_Text_16_24_tw,
                        'bg-accentroLightGreen-full text-accentroGray-full w-max h-auto px-px-8 py-px-4 font-semibold mt-px-24'
                      )}
                    >
                      {property.greenRectangle}
                    </div>
                  ) : (
                    <div className="h-[52px]" />
                  )}
                  <div className="mt-px-12">
                    <div className="flex flex-row">
                      <PropertyPrice>{property.price}</PropertyPrice>
                      <PropertyHeading className="m-px-8">EUR</PropertyHeading>
                    </div>
                    <Text_16_24 className="text-accentroWhite-500">
                      {property.pricePerSqm} EUR per m²
                    </Text_16_24>
                  </div>
                </PropertyPreviewDiv>
              </ContentWrapper>
            </Column>
          ))}
        </TwoColumnContainer>
        <div className="flex mt-[52px] md:mt-[65px]">
          <SimpleTextLink
            target="_blank"
            href="https://accentro.de/en/real-estate"
            className="font-semibold md:text-xl flex items-center justify-end"
          >
            See all ACCENTRO Properties
            <ArrowRight className="w-px-32 h-px-32 ml-px-12" />
          </SimpleTextLink>
        </div>
      </div>
    </GridWithGaps>
  )
}
